import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Minimize2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import './PenaltyQuizGame.css';

const PenaltyQuizGame = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quizId = searchParams.get('quizId');
  const iframeRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const containerRef = useRef(null);
  const toolbarTimeoutRef = useRef(null);

  const saveQuiz = (quizData) => {
    try {
      const data = localStorage.getItem('penaltyQuizDatabase');
      const quizzes = data ? JSON.parse(data) : [];
      
      const newQuiz = {
        id: quizData.id || Date.now().toString(),
        title: quizData.title || 'Penalty Quiz',
        questions: quizData.questions || [],
        createdAt: quizData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Check if quiz already exists
      const existingIndex = quizzes.findIndex(q => q.id === newQuiz.id);
      if (existingIndex >= 0) {
        quizzes[existingIndex] = newQuiz;
      } else {
        quizzes.push(newQuiz);
      }

      localStorage.setItem('penaltyQuizDatabase', JSON.stringify(quizzes));
      toast.success('Quiz saved successfully!');
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast.error('Failed to save quiz');
    }
  };

  const exitFullscreenAndNavigate = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.log('Exit fullscreen failed:', error);
    }
    navigate('/penalty-quiz-hub');
  };

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.log('Exit fullscreen failed:', error);
    }
  };

  // Auto-enter fullscreen on mount
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (containerRef.current && !document.fullscreenElement) {
          await containerRef.current.requestFullscreen();
          setIsFullscreen(true);
        }
      } catch (error) {
        console.log('Fullscreen request failed:', error);
      }
    };

    // Small delay to ensure component is mounted
    const timer = setTimeout(enterFullscreen, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // If we have a quizId, load the quiz data and send it to the iframe
    if (quizId) {
      const data = localStorage.getItem('penaltyQuizDatabase');
      const quizzes = data ? JSON.parse(data) : [];
      const quiz = quizzes.find(q => q.id === quizId);
      
      if (quiz && iframeRef.current) {
        // Wait for iframe to load, then send the quiz data
        const iframe = iframeRef.current;
        iframe.onload = () => {
          iframe.contentWindow.postMessage({
            type: 'LOAD_QUIZ',
            quiz: quiz
          }, '*');
        };
      }
    }

    // Listen for messages from the iframe
    const handleMessage = (event) => {
      if (event.data.type === 'SAVE_QUIZ') {
        saveQuiz(event.data.quiz);
      } else if (event.data.type === 'EXIT_GAME') {
        exitFullscreenAndNavigate();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [quizId, navigate, exitFullscreenAndNavigate]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle toolbar visibility - show on hover at top edge
  const handleToolbarAreaHover = () => {
    setShowToolbar(true);
    
    // Clear existing timeout
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
  };

  const handleToolbarMouseEnter = () => {
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
    setShowToolbar(true);
  };

  const handleToolbarMouseLeave = () => {
    toolbarTimeoutRef.current = setTimeout(() => {
      setShowToolbar(false);
    }, 1000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toolbarTimeoutRef.current) {
        clearTimeout(toolbarTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="penalty-quiz-game-page" ref={containerRef}>
      {/* Small trigger area at top edge - hover to show toolbar */}
      <div 
        className="toolbar-trigger-area"
        onMouseEnter={handleToolbarAreaHover}
      />
      
      {/* Transparent auto-hide toolbar */}
      <div 
        className={`game-toolbar ${showToolbar ? 'visible' : 'hidden'}`}
        onMouseEnter={handleToolbarMouseEnter}
        onMouseLeave={handleToolbarMouseLeave}
      >
        <Button 
          onClick={exitFullscreenAndNavigate} 
          className="back-button"
          variant="ghost"
        >
          <ArrowLeft size={20} />
          Back to Hub
        </Button>
        <div className="toolbar-title">
          <img src="/penalty-quiz-logo.png" alt="Penalty Quiz" className="toolbar-logo" />
          <span>Penalties Quiz Game</span>
        </div>
        <div className="toolbar-actions">
          {isFullscreen && (
            <Button onClick={exitFullscreen} variant="ghost" className="fullscreen-btn">
              <Minimize2 size={20} />
              Exit Fullscreen
            </Button>
          )}
        </div>
      </div>
      
      <div className="game-container">
        <iframe
          ref={iframeRef}
          src="/penalty-quiz-standalone.html"
          title="Penalty Quiz Game"
          className="game-iframe"
          allow="fullscreen"
        />
      </div>
    </div>
  );
};

export default PenaltyQuizGame;
